import { computed } from "vue";
import type { TableContext, ColumnWidth } from "./useTable.types";

/**
 * Table layout composable.
 *
 * Derives the CSS grid layout and scroll styles from table state.
 * Depends on column widths from `useTableResize` via `getStoredColumnWidths`.
 *
 * @param ctx - Shared table context from `useTableContext()`.
 * @param getStoredColumnWidths - Reader for the current column widths (from `useTableResize`).
 */
export const useTableLayout = (ctx: TableContext, getStoredColumnWidths: () => Map<number, ColumnWidth>) => {
  const { tableProps, headerCells } = ctx;

  /**
   * The `grid-template-columns` CSS value based on header cell widths.
   * Combines stored resize widths with static `width` props, defaulting to `auto`.
   */
  const gridTemplateColumns = computed(() => {
    const cells = headerCells.value;
    if (cells.length === 0) return "";

    const widths = getStoredColumnWidths();

    return cells
      .map((_cell, index) => {
        const stored = widths.get(index);
        if (stored) return `${stored.width}px`;
        const cellWidth = _cell?.props?.width;
        return cellWidth ? `${cellWidth}px` : "auto";
      })
      .join(" ");
  });

  /** The scroll mode from the parent table. */
  const scrollMode = computed(() => tableProps.value?.scroll);

  /**
   * The inline `grid-template-columns` style for the table root element.
   * Returns an empty string if no header cells are found.
   */
  const columnWidthStyle = computed(() => {
    const cols = gridTemplateColumns.value;
    return cols ? `grid-template-columns: ${cols};` : "";
  });

  /**
   * Max height inline style for the table body when scrolling is enabled.
   * Returns `undefined` if scroll is not active.
   */
  const bodyMaxHeightStyle = computed(() => {
    if (!scrollMode.value) return;
    return { "max-height": tableProps.value?.maxHeight ?? "400px" };
  });

  return {
    scrollMode,
    columnWidthStyle,
    bodyMaxHeightStyle,
  };
};
